// Chat's UI variant call (PLAN-20260929 slice 2). Chat shares the admin
// console's resolution (owner ruling 2026-09-29), so it calls the same
// governance endpoint through its own API client.
import {
  UI_VARIANT_RESOLUTION_TIMEOUT_MS,
  type UIVariant,
  type UIVariantResolution,
  type UIVariantRolloutLevel,
  type UIVariantService,
} from '@agion/ui/ui-variant'
import { apiClient } from '../../services/api-client'

/** The governance service's snake_case body; chat's API client returns it as sent. */
export interface UIVariantResolutionWire {
  organization_id: string
  effective_variant: UIVariant
  can_switch: boolean
  policy_level: UIVariantRolloutLevel
  policy_revision: number
  user_choice?: UIVariant
  environment_locked: boolean
  code: string
}

/** The console's client converts the body to camelCase; chat maps it here. */
export function fromWire(wire: UIVariantResolutionWire): UIVariantResolution {
  return {
    organizationId: wire.organization_id,
    effectiveVariant: wire.effective_variant,
    canSwitch: wire.can_switch,
    policyLevel: wire.policy_level,
    policyRevision: wire.policy_revision,
    ...(wire.user_choice ? { userChoice: wire.user_choice } : {}),
    environmentLocked: wire.environment_locked,
    code: wire.code,
  }
}

/** Resolve the calling member's variant. Bounded by the shared timeout and by the caller's signal; never retried. */
export function getUIVariantResolution(signal?: AbortSignal): Promise<UIVariantResolution> {
  const request = apiClient.getCancellable<UIVariantResolutionWire>('/v1/governance/ui-variant')
  const timer = setTimeout(() => request.abort(), UI_VARIANT_RESOLUTION_TIMEOUT_MS)
  const onAbort = () => request.abort()
  signal?.addEventListener('abort', onAbort, { once: true })
  return request.promise.then(fromWire).finally(() => {
    clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
  })
}

export const chatUIVariantService: UIVariantService = {
  getResolution: (signal) => getUIVariantResolution(signal),
}
